import React, { useEffect } from 'react';
import ScrollableComponent from '../../../components/base/ScrollableComponent';
import CreateCouponStepOne from './CreateCouponStepOne';
import CreateCouponStepTwo from './CreateCouponStepTwo';
import CustomBreadcrumb from '../../../components/base/CustomBreadcrumb';
import { useSelector, useDispatch } from 'react-redux';
import { createCouponBreads } from '../../../config/CustomBreadConfig';
import { useLocation, useParams, useHistory } from 'react-router-dom';
import { createAction } from '../../../utils';
import { sessionDataKey } from '../../../models/CreateCouponModel';
import {
  getCmapaignCreateSucessStepBar,
  GENERATE_COUPON,
  CRATE_COUPON_SET,
} from '../../../components/base/prompt/NextStepConfig';
import { APIStatus, CampaignType } from '../../../config/CustomEnums';
import { PermissionCodes } from '../../../config/PermissionCodes';
import { CouponURLFragments } from '../../../components/coupon/CouponUrlConfig';
import { TAB_LIST_OF_COUPON_SETS } from './CouponList';
import GenerateCouponView from '../../../components/coupon/couponCreation/GenerateCouponView';

function CreateCoupon() {
  const location = useLocation();
  const params = useParams();
  const dispatch = useDispatch();
  const history = useHistory();
  const { id } = params;

  const {
    stepConfig,
    currentStep,
    createCouponTemplateStatus,
    generateCouponStatus,
    couponQuantity,
    couponGenerated,
    couponPK,
    couponFormat,
  } = useSelector((state) => ({
    stepConfig: state.createCoupon.stepConfig,
    currentStep: state.createCoupon.currentStep,
    createCouponTemplateStatus: state.createCoupon.createCouponTemplateStatus,
    generateCouponStatus: state.createCoupon.generateCouponStatus,
    couponQuantity: state.createCoupon.couponQuantity,
    couponGenerated: state.createCoupon.couponGenerated,
    couponPK: state.createCoupon.couponTemplate.pk,
    couponFormat: state.createCoupon.couponTemplate.formats,
  }));

  const stepSet = [<CreateCouponStepOne />, <CreateCouponStepTwo />];

  useEffect(() => {
    if (id) {
      dispatch(
        createAction('couponList/getCouponSet')({ couponSetPK: params.id }),
      );
    } else {
      dispatch(createAction('createCoupon/loadCouponFromCookie')());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, id]);

  useEffect(() => {
    return () => {
      dispatch({ type: 'createCoupon/clearState' });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const generateCouponButton = (title) => {
    return {
      text: title,
      action: () => {
        dispatch({
          type: 'createCoupon/updateCouponTemplate',
          payload: { generateCoupon: true, notSaveToSession: true },
        });
        dispatch({
          type: 'createCoupon/updateState',
          payload: {
            generateCouponStatus: APIStatus.none,
            createCouponTemplateStatus: APIStatus.none,
          },
        });
      },
    };
  };

  const goToCouponList = () => {
    history.push({ pathname: '/coupons', hash: TAB_LIST_OF_COUPON_SETS });
  };

  const goToSignleCouponList = () => {
    history.push({
      pathname: `/coupons/${couponPK}`,
      hash: CouponURLFragments.list,
    });
  };

  const mayBeLaterAction = () => {
    if (couponGenerated) {
      goToSignleCouponList();
      return;
    }
    goToCouponList();
  };

  const getNextStepConfig = () => {
    let title = 'Successfully Create!';
    let description =
      'The coupon set is successfully created. Let’s Generate some coupons base this coupon set.';
    let type = CRATE_COUPON_SET;
    const stepNames = getCmapaignCreateSucessStepBar(
      CampaignType.couponsCampaign,
      true,
    );
    let buttons = [generateCouponButton('Generate coupon')];
    if (couponGenerated) {
      type = GENERATE_COUPON;
      title = 'Successfully Generate!';
      description = `${couponQuantity} Coupon is successfully generted. Let’s create a coupon campaign based on these coupon or you can generate a single coupon to a customer.`;
      buttons = [
        {
          text: 'Create Coupon campaign',
          action: () =>
            history.push({
              pathname: '/campaigns/create',
              search: `type=${CampaignType.couponsCampaign}&id=${couponPK}`,
            }),
          requires: PermissionCodes.addCampaign,
        },
        generateCouponButton('Continue generating coupon'),
      ];
    }
    buttons.push({
      text: 'Maybe Later',
      action: mayBeLaterAction,
    });
    return {
      title,
      description,
      type,
      stepNames,
      buttons,
    };
  };

  return (
    <>
      <ScrollableComponent
        tempSave={(save) => {
          dispatch({
            type: 'createCoupon/saveOrRemoveCouponFromCookie',
            payload: save,
          });
        }}
        nextStepConfig={{
          title: getNextStepConfig().title,
          description: getNextStepConfig().description,
          steps: {
            stepNames: getNextStepConfig().stepNames,
            current: getNextStepConfig().type,
          },
          buttons: getNextStepConfig().buttons,
        }}
        showFinishPop={
          generateCouponStatus === APIStatus.success ||
          createCouponTemplateStatus === APIStatus.success
        }
        continueCreateConfig={{
          onConfirm: () => {
            history.push(history.location.state?.from);
          },
          onCancel: mayBeLaterAction,
          onClose: mayBeLaterAction,
        }}
        sessionDataKey={sessionDataKey}
        content={stepSet}
        currentStep={currentStep}
        breadcrumb={
          <CustomBreadcrumb breadcrumbs={createCouponBreads(params?.id)} />
        }
        stepsConfig={stepConfig}
        caution={{
          detail: '', //Coupon set stores the content and controls a list of couopn.After you create a coupon set, you can general a list of single coupons for your customers
          title: 'Coupon Set',
        }}
      />
      <GenerateCouponView
        couponFormat={couponFormat}
        couponTemplate={couponPK}
      />
    </>
  );
}

export default CreateCoupon;
